import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Chip,
  Stack,
  Pagination,
  Alert,
  IconButton,
  Tooltip,
  TextField,
} from '@mui/material';
import {
  ArrowBack,
  Refresh,
  Cancel,
  Replay,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { examinationAPI, branchAPI, courseAPI, batchAPI } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';

const RejectedStudents = () => {
  const navigate = useNavigate();
  const { isSuperAdmin, user } = useAuth();
  const [loading, setLoading] = useState(false);

  // Filters
  const [selectedBranch, setSelectedBranch] = useState('');
  const [selectedExamination, setSelectedExamination] = useState('');
  const [selectedCourse, setSelectedCourse] = useState('');
  const [selectedBatch, setSelectedBatch] = useState('');
  const [search, setSearch] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Data
  const [rejectedStudents, setRejectedStudents] = useState([]);
  const [branches, setBranches] = useState([]);
  const [examinations, setExaminations] = useState([]);
  const [courses, setCourses] = useState([]);
  const [batches, setBatches] = useState([]);

  useEffect(() => {
    if (isSuperAdmin) {
      fetchBranches();
    }
    fetchExaminations();
    fetchCourses();
    fetchBatches();
  }, []);

  useEffect(() => {
    fetchRejectedStudents();
  }, [selectedBranch, selectedExamination, selectedCourse, selectedBatch, search, startDate, endDate, page]);

  const fetchBranches = async () => {
    try {
      const response = await branchAPI.getAll({ limit: 1000 });
      setBranches(response.data.data || response.data.branches || []);
    } catch (error) {
      toast.error('Failed to load branches');
    }
  };

  const fetchExaminations = async () => {
    try {
      const response = await examinationAPI.getAll();
      setExaminations(response.data.data || response.data.examinations || []);
    } catch (error) {
      toast.error('Failed to load examinations');
    }
  };

  const fetchCourses = async () => {
    try {
      const response = await courseAPI.getAll({ limit: 1000 });
      setCourses(response.data.data || []);
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  };

  const fetchBatches = async () => {
    try {
      const response = await batchAPI.getAll();
      setBatches(response.data.data?.batches || []);
    } catch (error) {
      console.error('Error fetching batches:', error);
    }
  };

  const fetchRejectedStudents = async () => {
    try {
      setLoading(true);
      const params = { page, limit: 50 };

      if (selectedBranch) params.branch = selectedBranch;
      if (selectedExamination) params.examination = selectedExamination;
      if (selectedCourse) params.course = selectedCourse;
      if (selectedBatch) params.batch = selectedBatch;
      if (search) params.search = search;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      const response = await examinationAPI.getRejectedStudentExams(params);

      setRejectedStudents(response.data.data || []);
      setTotalPages(response.data.pagination?.pages || 1);
      setTotalCount(response.data.pagination?.total || 0);
    } catch (error) {
      toast.error('Failed to load rejected students');
    } finally {
      setLoading(false);
    }
  };

  const handleResubmit = async (studentExamId) => {
    try {
      await examinationAPI.resubmitStudentExam(studentExamId);
      toast.success('Application resubmitted successfully for re-approval');
      fetchRejectedStudents();
    } catch (error) {
      toast.error('Failed to resubmit application');
    }
  };

  const clearFilters = () => {
    setSelectedBranch('');
    setSelectedExamination('');
    setSelectedCourse('');
    setSelectedBatch('');
    setSearch('');
    setStartDate('');
    setEndDate('');
    setPage(1);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={() => navigate('/admin/examinations')}
          >
            Back
          </Button>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              Rejected Students
            </Typography>
            <Typography variant="body2" color="text.secondary">
              View rejected examination applications and resubmit for approval
            </Typography>
          </Box>
        </Box>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={fetchRejectedStudents}
        >
          Refresh
        </Button>
      </Box>

      {/* Filters */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Filters
          </Typography>
          <Button size="small" onClick={clearFilters}>
            Clear Filters
          </Button>
        </Box>
        <Grid container spacing={3}>
          {isSuperAdmin && (
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Branch</InputLabel>
                <Select
                  value={selectedBranch}
                  onChange={(e) => {
                    setSelectedBranch(e.target.value);
                    setPage(1);
                  }}
                  label="Branch"
                  MenuProps={{ PaperProps: { style: { maxHeight: 320, overflowY: 'auto' } } }}
                >
                  <MenuItem value="">All Branches</MenuItem>
                  {branches.map((branch) => (
                    <MenuItem key={branch._id} value={branch._id}>
                      {branch.name} ({branch.code})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Examination</InputLabel>
              <Select
                value={selectedExamination}
                onChange={(e) => {
                  setSelectedExamination(e.target.value);
                  setPage(1);
                }}
                label="Examination"
              >
                <MenuItem value="">All Examinations</MenuItem>
                {examinations.map((exam) => (
                  <MenuItem key={exam._id} value={exam._id}>
                    {exam.name} ({exam.code})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Course</InputLabel>
              <Select
                value={selectedCourse}
                onChange={(e) => {
                  setSelectedCourse(e.target.value);
                  setPage(1);
                }}
                label="Course"
                MenuProps={{ PaperProps: { style: { maxHeight: 320, overflowY: 'auto' } } }}
              >
                <MenuItem value="">All Courses</MenuItem>
                {courses.map((course) => (
                  <MenuItem key={course._id} value={course._id}>
                    {course.name} ({course.code})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Batch</InputLabel>
              <Select
                value={selectedBatch}
                onChange={(e) => {
                  setSelectedBatch(e.target.value);
                  setPage(1);
                }}
                label="Batch"
                MenuProps={{ PaperProps: { style: { maxHeight: 320, overflowY: 'auto' } } }}
              >
                <MenuItem value="">All Batches</MenuItem>
                {batches.map((b) => (
                  <MenuItem key={b._id} value={b._id}>
                    {b.name} ({b.year})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="Search"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Name or Form Number"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              type="date"
              label="Start Date"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                setPage(1);
              }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              type="date"
              label="End Date"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                setPage(1);
              }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Results */}
      <Paper elevation={2} sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Rejected Applications ({totalCount})
          </Typography>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : rejectedStudents.length === 0 ? (
          <Alert severity="info">
            No rejected applications found. Use the filters above to refine your search.
          </Alert>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Form No.</strong></TableCell>
                    <TableCell><strong>Student Name</strong></TableCell>
                    <TableCell><strong>Examination</strong></TableCell>
                    <TableCell><strong>Branch</strong></TableCell>
                    <TableCell><strong>Course</strong></TableCell>
                    <TableCell><strong>Rejection Reason</strong></TableCell>
                    <TableCell><strong>Rejected On</strong></TableCell>
                    <TableCell><strong>Resubmissions</strong></TableCell>
                    <TableCell align="center"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rejectedStudents.map((studentExam) => (
                    <TableRow key={studentExam._id}>
                      <TableCell>{studentExam.student?.formNumber || 'N/A'}</TableCell>
                      <TableCell>
                        {studentExam.student?.personalInfo?.nameAsPerTC || 'N/A'}
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {studentExam.examination?.name || 'N/A'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {studentExam.examination?.code || ''}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{studentExam.branch?.name || 'N/A'}</TableCell>
                      <TableCell>
                        {studentExam.student?.course?.name || 'N/A'}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="error">
                          {studentExam.rejectionReason || 'Not specified'}
                        </Typography>
                      </TableCell>
                      <TableCell>{formatDate(studentExam.updatedAt)}</TableCell>
                      <TableCell align="center">
                        <Chip
                          label={studentExam.resubmissionCount || 0}
                          size="small"
                          color={studentExam.resubmissionCount > 0 ? 'warning' : 'default'}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Resubmit for approval">
                          <IconButton
                            color="primary"
                            size="small"
                            onClick={() => handleResubmit(studentExam._id)}
                          >
                            <Replay />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(e, value) => setPage(value)}
                  color="primary"
                />
              </Box>
            )}
          </>
        )}
      </Paper>
    </Box>
  );
};

export default RejectedStudents;
